/**
 * Durable per-row drill-down for a single uploaded file (file_sha256).
 */

import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks/redux';
import {
  fetchPaperJournalUploadRowsThunk,
  selectPaperJournalUploadRows,
  selectPaperJournalUploadRowsError,
  selectPaperJournalUploadRowsStatus,
} from '@/app/store/paperJournalSlice';
import { PaperJournalExpandableRowList } from '@/pages/PaperJournal/PaperJournalExpandableRowList/PaperJournalExpandableRowList';

import './PaperJournalUploadDetail.scss';

export const PaperJournalUploadDetail: React.FC = () => {
  const { fileSha256 } = useParams<{ fileSha256: string }>();
  const dispatch = useAppDispatch();
  const rows = useAppSelector(selectPaperJournalUploadRows);
  const status = useAppSelector(selectPaperJournalUploadRowsStatus);
  const error = useAppSelector(selectPaperJournalUploadRowsError);

  useEffect(() => {
    if (fileSha256 !== undefined && fileSha256 !== '') {
      void dispatch(fetchPaperJournalUploadRowsThunk(fileSha256));
    }
  }, [dispatch, fileSha256]);

  if (fileSha256 === undefined || fileSha256 === '') {
    return (
      <div className="paper-journal-upload-detail">
        <nav className="paper-journal-upload-detail__back-nav" aria-label="Paper journal navigation">
          <Link to="/paper-journal" className="btn-back-link">
            ← Back to Paper Journal
          </Link>
        </nav>
        <p className="paper-journal-upload-detail__error">Missing file identifier.</p>
      </div>
    );
  }

  const loading = status === 'loading';
  const failed = status === 'failed';

  return (
    <div className="paper-journal-upload-detail">
      <nav className="paper-journal-upload-detail__back-nav" aria-label="Paper journal navigation">
        <Link to="/paper-journal" className="btn-back-link">
          ← Back to Paper Journal
        </Link>
      </nav>

      <header className="paper-journal-upload-detail__header">
        <h1 className="paper-journal-upload-detail__title">Paper journal rows</h1>
        <div className="paper-journal-upload-detail__subtitle mono">{fileSha256}</div>
      </header>

      {failed && error !== null && (
        <div className="paper-journal-upload-detail__banner" role="alert">
          {error}
        </div>
      )}

      <PaperJournalExpandableRowList
        rows={rows}
        loading={loading}
        emptyMessage="No rows returned for this upload."
      />
    </div>
  );
};

export default PaperJournalUploadDetail;
